import { cleanURLWithLang } from "@/lib/url/cleanURLWithLang"
import { STRICT_FILTER_TYPES } from "@/lib/locale/strictMode"

/** The NavigationItemLink GraphQL fragment doesn't select `__typename`, so
 *  identify link entries by their `pageLink` field when the typename is absent. */
export function isNavigationItemLink(item: any): boolean {
  if (!item) return false
  if (item.__typename) return item.__typename === "NavigationItemLink"
  return "pageLink" in item
}

/** Strict-locale filter for the direct children of a navigation menu. */
export function filterMenuItemsForStrictLocale(
  items: any[] | null | undefined,
  isPageTranslated: (pageLink: any) => boolean,
) {
  return (items ?? []).filter((sub: any) => {
    if (!sub) return false
    if (isNavigationItemLink(sub)) return isPageTranslated(sub?.pageLink)
    if (STRICT_FILTER_TYPES.includes(sub.__typename))
      return isPageTranslated(sub)
    if (sub.__typename === "NavigationSubcategory") {
      const children = sub?.navigationItemsCollection?.items
      if (!children?.length) return false
      return children.some(
        (child: any) =>
          isNavigationItemLink(child) && isPageTranslated(child?.pageLink),
      )
    }
    return true
  })
}

/** Format nav category links. cleanURLFn is optional; when not provided, cleanURLWithLang(item, locale) is used.
 *  isPageTranslatedFn is optional; when provided, filters tertiary nav items by locale tag. */
export function formatCategoryLinks(
  categoryLinks: any,
  locale: string,
  cleanURLFn?: (item: any) => string,
  isPageTranslatedFn?: (pageLink: any) => boolean,
) {
  const cleanUrl = cleanURLFn ?? ((item: any) => cleanURLWithLang(item, locale))
  const mapped = (categoryLinks ?? [])
    .filter(Boolean)
    .map((secondaryItem: any) => {
      let subCategoryLinkData = null
      if (secondaryItem?.subCategoryLink?.title) {
        subCategoryLinkData = {
          text: secondaryItem?.subCategoryLink?.title,
          description: secondaryItem?.subCategoryLink?.description,
          url: cleanUrl(secondaryItem?.subCategoryLink),
        }
      }

      let tertiaryItems = secondaryItem?.navigationItemsCollection?.items
      if (tertiaryItems) {
        tertiaryItems = tertiaryItems.filter((item: any) => {
          if (!item) return false
          if (isPageTranslatedFn && isNavigationItemLink(item))
            return isPageTranslatedFn(item?.pageLink)
          return true
        })
      }

      return {
        contentfulId: secondaryItem?.sys?.id,
        text: secondaryItem?.title || secondaryItem?.text,
        type: tertiaryItems?.length > 0 ? "category" : "link",
        description: secondaryItem?.description,
        subCategoryLink: subCategoryLinkData,
        isLeaf: !secondaryItem?.navigationItemsCollection,
        url: cleanUrl(secondaryItem),
        navSubcategoryPromo: secondaryItem?.featuredPromo
          ? {
              title: secondaryItem?.featuredPromo?.title,
              description: secondaryItem?.featuredPromo?.description,
              pageLink: {
                url: cleanUrl(secondaryItem?.featuredPromo),
                text:
                  secondaryItem?.featuredPromo?.pageLinkLabel ||
                  secondaryItem?.featuredPromo?.pageLink?.text,
              },
              image: {
                src: secondaryItem?.featuredPromo?.image?.image?.url,
                altText: secondaryItem?.featuredPromo?.image?.altText,
              },
            }
          : null,
        links: tertiaryItems?.map((tertiaryItem: any) => {
          return {
            text: tertiaryItem?.title,
            type: "link",
            description: tertiaryItem?.description,
            contentfulId: tertiaryItem?.sys?.id,
            url: cleanUrl(tertiaryItem),
          }
        }),
      }
    })

  if (!isPageTranslatedFn) return mapped
  // Drop subcategories emptied by filtering; keep standalone (already
  // filtered) links, which have no child collection.
  return mapped?.filter(
    (item: any) => item?.isLeaf || item?.links?.length > 0,
  )
}

/**
 * Server-safe: formats a nav category variant without LinkComponent.
 * Use this when formatting on the server so no functions are passed to Client Components.
 */
export function formatNavCategoryVariantForServer(
  variant: any,
  locale: string,
  isPageTranslatedFn?: (pageLink: any) => boolean,
) {
  const cleanUrl = (item: any) => cleanURLWithLang(item, locale)
  const menuItems = isPageTranslatedFn
    ? filterMenuItemsForStrictLocale(
        variant?.navigationItemsCollection?.items,
        isPageTranslatedFn,
      )
    : variant?.navigationItemsCollection?.items
  return {
    contentfulId: variant?.sys?.id,
    text: variant?.title,
    url: cleanUrl(variant),
    type: menuItems?.length > 0 ? "category" : "link",
    id: variant?.sys?.id,
    locale: locale,
    categoryLink: {
      text: variant?.categoryLink?.title,
      description: variant?.categoryLink?.description,
      url: variant?.categoryLink?.pageLink?.slug
        ? cleanUrl(variant?.categoryLink)
        : undefined,
    },
    navCategoryPromo: {
      title: variant?.featuredPromo?.title,
      description: variant?.featuredPromo?.description,
      pageLink: {
        url: cleanUrl(variant?.featuredPromo),
        text:
          variant?.featuredPromo?.pageLinkLabel ||
          variant?.featuredPromo?.pageLink?.text,
      },
      image: {
        src: variant?.featuredPromo?.image?.image?.url,
        altText: variant?.featuredPromo?.image?.altText,
      },
    },
    links: formatCategoryLinks(
      menuItems,
      locale,
      undefined,
      isPageTranslatedFn,
    ),
  }
}

/** Formats a nav category variant for use in Client Components (includes LinkComponent). */
export function formatNavCategoryVariant(
  variant: any,
  locale: string,
  Link: any,
) {
  return {
    ...formatNavCategoryVariantForServer(variant, locale),
    LinkComponent: Link,
  }
}
